import type { Locale } from "@llmgateway/i18n";

export type Competitor = "openrouter" | "litellm" | "portkey";
export type CompareValue = boolean | string;

export interface HeroContent {
	heading: string;
	description: string;
	badges?: string[];
	cta: {
		primary: {
			text: string;
			href: string;
			external?: boolean;
		};
		secondary: {
			text: string;
			href: string;
			external?: boolean;
		};
	};
}

export interface ComparisonFeature {
	title: string;
	description: string;
	llmgateway: CompareValue;
	competitor: CompareValue;
}

export interface ComparisonCategory {
	category: string;
	features: ComparisonFeature[];
}

export interface ComparisonContent {
	eyebrow: string;
	title: string;
	description: string;
	whyTitle: string;
	whyBullets: string[];
	llmgatewayCard: {
		subtitle: string;
		price: string;
		note: string;
	};
	competitorCard: {
		name: string;
		subtitle: string;
		price: string;
		note: string;
	};
	primaryCta: string;
	secondaryCta: string;
	footnote: string;
	categories: ComparisonCategory[];
}

export interface CompareMetadata {
	title: string;
	description: string;
	openGraphTitle: string;
	openGraphDescription: string;
	twitterTitle: string;
	twitterDescription: string;
}

const heroContent: Record<Locale, Record<Competitor, HeroContent>> = {
	en: {
		openrouter: {
			heading: "Why Choose doteb Over OpenRouter?",
			description:
				"Compare our unified API gateway with advanced routing, analytics, and cost optimization against OpenRouter's basic proxy service.",
			badges: [
				"Advanced Analytics",
				"Smart Routing",
				"Cost Optimization",
				"Enterprise Ready",
			],
			cta: {
				primary: { text: "Start for Free", href: "/signup" },
				secondary: {
					text: "View Documentation",
					href: "https://docs.doteb.com",
					external: true,
				},
			},
		},
		litellm: {
			heading: "Why Choose doteb Over LiteLLM?",
			description:
				"Compare our production-ready managed gateway with advanced analytics, routing, and enterprise features against LiteLLM's self-hosted proxy solution.",
			badges: [
				"Managed Infrastructure",
				"Advanced Analytics",
				"Enterprise Support",
				"Production Ready",
			],
			cta: {
				primary: { text: "Start for Free", href: "/signup" },
				secondary: {
					text: "View Documentation",
					href: "https://docs.doteb.com",
					external: true,
				},
			},
		},
		portkey: {
			heading: "Looking for a Portkey Alternative?",
			description:
				"Compare doteb's full-platform deployment, automatic provider routing, and transparent pricing against Portkey's gateway and LLMOps suite.",
			badges: [
				"Full-Platform Deploy",
				"Automatic Routing",
				"Image & Video Gen",
				"Transparent Pricing",
			],
			cta: {
				primary: { text: "Start for Free", href: "/signup" },
				secondary: {
					text: "View Documentation",
					href: "https://docs.doteb.com",
					external: true,
				},
			},
		},
	},
	"zh-CN": {
		openrouter: {
			heading: "为什么选择 doteb，而不是 OpenRouter？",
			description:
				"对比 doteb 的统一 API、智能路由、分析和成本优化能力，与 OpenRouter 的基础代理服务差异。",
			badges: ["高级分析", "智能路由", "成本优化", "企业就绪"],
			cta: {
				primary: { text: "免费开始", href: "/signup" },
				secondary: {
					text: "查看文档",
					href: "https://docs.doteb.com",
					external: true,
				},
			},
		},
		litellm: {
			heading: "为什么选择 doteb，而不是 LiteLLM？",
			description:
				"对比 doteb 面向生产的托管网关、分析、路由和企业能力，与 LiteLLM 自托管代理方案的差异。",
			badges: ["托管基础设施", "高级分析", "企业支持", "生产就绪"],
			cta: {
				primary: { text: "免费开始", href: "/signup" },
				secondary: {
					text: "查看文档",
					href: "https://docs.doteb.com",
					external: true,
				},
			},
		},
		portkey: {
			heading: "正在寻找 Portkey 替代方案？",
			description:
				"对比 doteb 的完整平台部署、自动供应商路由和透明计费，与 Portkey 的 gateway 和 LLMOps 套件差异。",
			badges: ["完整平台部署", "自动路由", "图像和视频生成", "透明计费"],
			cta: {
				primary: { text: "免费开始", href: "/signup" },
				secondary: {
					text: "查看文档",
					href: "https://docs.doteb.com",
					external: true,
				},
			},
		},
	},
};

const commonCompareCopy = {
	en: {
		eyebrow: "Compare platforms",
		title: "Find the perfect fit",
		whyTitle: "Why choose doteb?",
		primaryCta: "Start Free with doteb",
		secondaryCta: "View Pricing Details",
		footnote:
			"No credit card required - Self-host option available - Enterprise support included",
		llmgatewayCard: {
			price: "From $0",
			note: "Self-host free forever",
		},
	},
	"zh-CN": {
		eyebrow: "平台对比",
		title: "找到更适合你的方案",
		whyTitle: "为什么选择 doteb？",
		primaryCta: "免费开始使用 doteb",
		secondaryCta: "查看价格详情",
		footnote: "无需信用卡 - 支持自托管 - 包含企业支持",
		llmgatewayCard: {
			price: "$0 起",
			note: "自托管可永久免费使用",
		},
	},
};

const comparisonContent: Record<
	Locale,
	Record<Competitor, ComparisonContent>
> = {
	en: {
		openrouter: {
			...commonCompareCopy.en,
			description: "Compare doteb and OpenRouter features side by side",
			whyBullets: [
				"Bring Your Own Keys at no extra cost",
				"Real-time analytics for cost & latency optimization",
				"Can be self hosted for complete control",
				"99.9% uptime SLA with enterprise support",
			],
			llmgatewayCard: {
				...commonCompareCopy.en.llmgatewayCard,
				subtitle: "OPEN & FLEXIBLE",
			},
			competitorCard: {
				name: "OpenRouter",
				subtitle: "CLOSED & 5.5% fee",
				price: "From $0",
				note: "Credit-based pricing",
			},
			categories: [
				{
					category: "Pricing & Fees",
					features: [
						{
							title: "Credits pricing",
							description: "Pay-as-you-go with credits",
							llmgateway: "Flat 5% fee on credit purchases",
							competitor: "5.5% platform fee",
						},
						{
							title: "Bring Your Own Keys",
							description: "Use your own provider API keys",
							llmgateway: "Free - pay providers directly",
							competitor: "1M free reqs/mo, then 5% fee",
						},
						{
							title: "Self-hosting option",
							description:
								"Deploy on your infrastructure for free (See license)",
							llmgateway: "Free for non-commercial use",
							competitor: false,
						},
					],
				},
				{
					category: "Analytics & Monitoring",
					features: [
						{
							title: "Real-time cost analytics",
							description: "Detailed cost tracking for every request",
							llmgateway: true,
							competitor: "Basic",
						},
						{
							title: "Latency analytics",
							description: "Real-time performance monitoring",
							llmgateway: true,
							competitor: "Basic",
						},
						{
							title: "Request-level insights",
							description: "Granular analytics for each API call",
							llmgateway: true,
							competitor: false,
						},
						{
							title: "Usage dashboard",
							description: "Comprehensive usage metrics",
							llmgateway: true,
							competitor: true,
						},
					],
				},
				{
					category: "Reliability & Support",
					features: [
						{
							title: "Uptime SLA",
							description: "Guaranteed uptime for managed instances",
							llmgateway: "99.9%",
							competitor: "Enterprise only",
						},
						{
							title: "Failover support",
							description: "Automatic failover to backup providers",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "Load balancing",
							description: "Distribute requests across providers",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "Priority support",
							description: "Dedicated support for paid plans",
							llmgateway: "Enterprise",
							competitor: false,
						},
					],
				},
			],
		},
		litellm: {
			...commonCompareCopy.en,
			description: "Compare doteb and LiteLLM features side by side",
			whyBullets: [
				"Managed infrastructure with 99.9% uptime SLA",
				"Advanced analytics with cost optimization insights",
				"Enterprise-ready with team collaboration & SSO",
				"Production-tested with built-in monitoring",
			],
			llmgatewayCard: {
				...commonCompareCopy.en.llmgatewayCard,
				subtitle: "MANAGED & PRODUCTION-READY",
			},
			competitorCard: {
				name: "LiteLLM",
				subtitle: "SELF-HOSTED ONLY",
				price: "Free",
				note: "Open source (MIT)",
			},
			categories: [
				{
					category: "Deployment & Infrastructure",
					features: [
						{
							title: "Managed infrastructure",
							description: "Fully managed, production-ready deployment",
							llmgateway: true,
							competitor: false,
						},
						{
							title: "Self-hosting option",
							description: "Deploy on your own infrastructure (See license)",
							llmgateway: "Free for non-commercial use",
							competitor: "Open source (MIT)",
						},
						{
							title: "Auto-scaling",
							description: "Automatic scaling based on traffic",
							llmgateway: true,
							competitor: "Manual setup required",
						},
						{
							title: "99.9% uptime SLA",
							description: "Guaranteed uptime for managed instances",
							llmgateway: true,
							competitor: false,
						},
					],
				},
				{
					category: "Analytics & Monitoring",
					features: [
						{
							title: "Real-time cost analytics",
							description: "Detailed cost tracking for every request",
							llmgateway: true,
							competitor: "Basic",
						},
						{
							title: "Latency analytics",
							description:
								"Real-time performance monitoring with visualizations",
							llmgateway: true,
							competitor: "Basic",
						},
						{
							title: "Request-level insights",
							description: "Granular analytics for each API call",
							llmgateway: true,
							competitor: "Requires custom setup",
						},
						{
							title: "Model usage dashboard",
							description: "Comprehensive model usage metrics and trends",
							llmgateway: true,
							competitor: "Requires integration",
						},
						{
							title: "Cost optimization insights",
							description: "AI-powered recommendations to reduce costs",
							llmgateway: true,
							competitor: false,
						},
					],
				},
				{
					category: "Enterprise Features",
					features: [
						{
							title: "Team collaboration",
							description: "Multi-user access with role-based permissions",
							llmgateway: true,
							competitor: "Requires custom setup",
						},
						{
							title: "Project isolation",
							description: "Separate projects with individual API keys",
							llmgateway: true,
							competitor: "Manual configuration",
						},
						{
							title: "Billing integration",
							description: "Built-in billing with Stripe integration",
							llmgateway: true,
							competitor: false,
						},
						{
							title: "Priority support",
							description: "Dedicated support for paid plans",
							llmgateway: "Pro+",
							competitor: "Community only",
						},
						{
							title: "SSO integration",
							description: "Enterprise single sign-on support",
							llmgateway: "Enterprise",
							competitor: false,
						},
					],
				},
				{
					category: "Developer Experience",
					features: [
						{
							title: "OpenAI-compatible API",
							description: "Drop-in replacement for OpenAI API",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "Interactive playground",
							description: "Test models directly in the browser",
							llmgateway: true,
							competitor: false,
						},
						{
							title: "API key management",
							description: "Create and manage multiple API keys",
							llmgateway: true,
							competitor: "Basic",
						},
						{
							title: "Request caching",
							description: "Built-in Redis caching for responses",
							llmgateway: true,
							competitor: "Manual setup required",
						},
						{
							title: "Comprehensive docs",
							description: "Detailed documentation and guides",
							llmgateway: true,
							competitor: true,
						},
					],
				},
			],
		},
		portkey: {
			...commonCompareCopy.en,
			title:
				"The best Portkey alternative for teams who want one open platform",
			description: "Compare doteb and Portkey features side by side",
			whyBullets: [
				"Self-host the entire platform, not just the router",
				"Automatic routing that scores providers on live metrics",
				"Image & video generation through the same API",
				"Transparent pricing - 5% platform fee, or 0% with your own keys",
			],
			llmgatewayCard: {
				...commonCompareCopy.en.llmgatewayCard,
				subtitle: "SELF-HOSTABLE & PRODUCTION-READY",
			},
			competitorCard: {
				name: "Portkey",
				subtitle: "GATEWAY + LLMOPS PLATFORM",
				price: "Usage-based",
				note: "Free tier, then paid plans",
			},
			categories: [
				{
					category: "Deployment",
					features: [
						{
							title: "Managed cloud",
							description: "Fully managed, production-ready hosted service",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "Self-host the full platform",
							description: "Run the entire stack on your own infrastructure",
							llmgateway: "Available",
							competitor: "Gateway only",
						},
						{
							title: "Deployment scope",
							description: "What can be deployed in your environment",
							llmgateway: "Full platform",
							competitor: "Gateway/router (MIT)",
						},
						{
							title: "One-command Docker deploy",
							description: "Single image with dashboard, worker, and gateway",
							llmgateway: true,
							competitor: "Gateway only",
						},
					],
				},
				{
					category: "Routing & Reliability",
					features: [
						{
							title: "Automatic provider scoring",
							description:
								"Routes on live uptime, throughput, price, and latency",
							llmgateway: true,
							competitor: "Manual config",
						},
						{
							title: "Failover & retries",
							description: "Transparent retry on a healthy provider",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "Load balancing",
							description: "Spread traffic across providers for a model",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "Response caching",
							description: "Built-in caching for repeated requests",
							llmgateway: "Redis, 10s-1yr TTL",
							competitor: "Incl. semantic",
						},
					],
				},
				{
					category: "Cost & Pricing",
					features: [
						{
							title: "Bring your own keys",
							description: "Use your own provider keys",
							llmgateway: "0% markup",
							competitor: true,
						},
						{
							title: "Transparent platform fee",
							description: "Predictable, easy-to-reason-about pricing",
							llmgateway: "5% or 0% (BYOK)",
							competitor: "Usage/seat tiers",
						},
						{
							title: "Real-time cost analytics",
							description: "Per-request cost tracking in the dashboard",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "Free to start",
							description: "Get going without a paid plan",
							llmgateway: "Free self-host",
							competitor: "Free tier (limited)",
						},
					],
				},
				{
					category: "Capabilities",
					features: [
						{
							title: "Model coverage",
							description: "Models and providers available through one API",
							llmgateway: "300+ models, 25+ providers",
							competitor: "1,600+ (vendor claim)",
						},
						{
							title: "Image & video generation",
							description: "Generative media through the same API",
							llmgateway: true,
							competitor: "Limited",
						},
						{
							title: "Guardrails",
							description:
								"Prompt injection, PII, jailbreak, and secret detection",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "Versioned prompt management",
							description: "Prompt registry, templates, and deployments",
							llmgateway: "Playground only",
							competitor: true,
						},
						{
							title: "OpenTelemetry & deep tracing",
							description: "Export traces into an existing observability stack",
							llmgateway: false,
							competitor: true,
						},
					],
				},
			],
		},
	},
	"zh-CN": {
		openrouter: {
			...commonCompareCopy["zh-CN"],
			description: "并排比较 doteb 和 OpenRouter 的能力",
			whyBullets: [
				"自带供应商密钥无需额外费用",
				"实时分析成本和延迟，便于优化",
				"可自托管，获得完整控制权",
				"企业支持下提供 99.9% uptime SLA",
			],
			llmgatewayCard: {
				...commonCompareCopy["zh-CN"].llmgatewayCard,
				subtitle: "开放且灵活",
			},
			competitorCard: {
				name: "OpenRouter",
				subtitle: "闭源且 5.5% 费用",
				price: "$0 起",
				note: "基于额度的计费",
			},
			categories: [
				{
					category: "价格与费用",
					features: [
						{
							title: "额度计费",
							description: "按使用量消耗额度",
							llmgateway: "购买额度固定收取 5%",
							competitor: "5.5% 平台费",
						},
						{
							title: "自带供应商密钥",
							description: "使用你自己的供应商 API key",
							llmgateway: "免费 - 直接向供应商付费",
							competitor: "每月 100 万次免费请求，之后收取 5%",
						},
						{
							title: "自托管选项",
							description: "免费部署到自己的基础设施（见许可证）",
							llmgateway: "非商业使用免费",
							competitor: false,
						},
					],
				},
				{
					category: "分析与监控",
					features: [
						{
							title: "实时成本分析",
							description: "每次请求都有详细成本追踪",
							llmgateway: true,
							competitor: "基础",
						},
						{
							title: "延迟分析",
							description: "实时性能监控",
							llmgateway: true,
							competitor: "基础",
						},
						{
							title: "请求级洞察",
							description: "每次 API 调用都有细粒度分析",
							llmgateway: true,
							competitor: false,
						},
						{
							title: "用量仪表盘",
							description: "完整的用量指标",
							llmgateway: true,
							competitor: true,
						},
					],
				},
				{
					category: "可靠性与支持",
					features: [
						{
							title: "Uptime SLA",
							description: "托管实例的可用性承诺",
							llmgateway: "99.9%",
							competitor: "仅企业版",
						},
						{
							title: "故障回退",
							description: "自动切换到备用供应商",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "负载均衡",
							description: "请求分散到多个供应商",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "优先支持",
							description: "付费方案提供专属支持",
							llmgateway: "企业版",
							competitor: false,
						},
					],
				},
			],
		},
		litellm: {
			...commonCompareCopy["zh-CN"],
			description: "并排比较 doteb 和 LiteLLM 的能力",
			whyBullets: [
				"托管基础设施，包含 99.9% uptime SLA",
				"高级分析和成本优化洞察",
				"团队协作与 SSO 等企业能力",
				"生产环境验证，并内置监控",
			],
			llmgatewayCard: {
				...commonCompareCopy["zh-CN"].llmgatewayCard,
				subtitle: "托管且生产就绪",
			},
			competitorCard: {
				name: "LiteLLM",
				subtitle: "仅自托管",
				price: "免费",
				note: "开源（MIT）",
			},
			categories: [
				{
					category: "部署与基础设施",
					features: [
						{
							title: "托管基础设施",
							description: "完全托管、生产可用的部署",
							llmgateway: true,
							competitor: false,
						},
						{
							title: "自托管选项",
							description: "部署到自己的基础设施（见许可证）",
							llmgateway: "非商业使用免费",
							competitor: "开源（MIT）",
						},
						{
							title: "自动扩缩容",
							description: "根据流量自动扩展",
							llmgateway: true,
							competitor: "需要手动配置",
						},
						{
							title: "99.9% uptime SLA",
							description: "托管实例的可用性承诺",
							llmgateway: true,
							competitor: false,
						},
					],
				},
				{
					category: "分析与监控",
					features: [
						{
							title: "实时成本分析",
							description: "每次请求都有详细成本追踪",
							llmgateway: true,
							competitor: "基础",
						},
						{
							title: "延迟分析",
							description: "带可视化的实时性能监控",
							llmgateway: true,
							competitor: "基础",
						},
						{
							title: "请求级洞察",
							description: "每次 API 调用都有细粒度分析",
							llmgateway: true,
							competitor: "需要自定义配置",
						},
						{
							title: "模型用量仪表盘",
							description: "完整的模型用量指标和趋势",
							llmgateway: true,
							competitor: "需要集成",
						},
						{
							title: "成本优化洞察",
							description: "AI 驱动的降本建议",
							llmgateway: true,
							competitor: false,
						},
					],
				},
				{
					category: "企业能力",
					features: [
						{
							title: "团队协作",
							description: "多用户访问和基于角色的权限",
							llmgateway: true,
							competitor: "需要自定义配置",
						},
						{
							title: "项目隔离",
							description: "项目独立、API key 独立",
							llmgateway: true,
							competitor: "手动配置",
						},
						{
							title: "计费集成",
							description: "内置 Stripe 计费",
							llmgateway: true,
							competitor: false,
						},
						{
							title: "优先支持",
							description: "付费方案提供专属支持",
							llmgateway: "Pro+",
							competitor: "仅社区",
						},
						{
							title: "SSO 集成",
							description: "企业单点登录支持",
							llmgateway: "企业版",
							competitor: false,
						},
					],
				},
				{
					category: "开发者体验",
					features: [
						{
							title: "OpenAI 兼容 API",
							description: "可直接替换 OpenAI API",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "交互式 Playground",
							description: "直接在浏览器测试模型",
							llmgateway: true,
							competitor: false,
						},
						{
							title: "API key 管理",
							description: "创建和管理多个 API key",
							llmgateway: true,
							competitor: "基础",
						},
						{
							title: "请求缓存",
							description: "内置 Redis 响应缓存",
							llmgateway: true,
							competitor: "需要手动配置",
						},
						{
							title: "完整文档",
							description: "详细文档和指南",
							llmgateway: true,
							competitor: true,
						},
					],
				},
			],
		},
		portkey: {
			...commonCompareCopy["zh-CN"],
			title: "需要一个开放平台的团队，更适合的 Portkey 替代方案",
			description: "并排比较 doteb 和 Portkey 的能力",
			whyBullets: [
				"可自托管整个平台，而不只是 router",
				"根据实时指标自动为供应商评分并路由",
				"同一个 API 支持图像和视频生成",
				"透明计费 - 5% 平台费，自带密钥则 0%",
			],
			llmgatewayCard: {
				...commonCompareCopy["zh-CN"].llmgatewayCard,
				subtitle: "可自托管且生产就绪",
			},
			competitorCard: {
				name: "Portkey",
				subtitle: "Gateway + LLMOps 平台",
				price: "按用量",
				note: "免费层，之后为付费方案",
			},
			categories: [
				{
					category: "部署",
					features: [
						{
							title: "托管云",
							description: "完全托管、生产可用的云服务",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "自托管完整平台",
							description: "在自己的基础设施上运行整套 stack",
							llmgateway: "可用",
							competitor: "仅 Gateway",
						},
						{
							title: "部署范围",
							description: "可部署到你环境中的范围",
							llmgateway: "完整平台",
							competitor: "Gateway/router（MIT）",
						},
						{
							title: "一条命令 Docker 部署",
							description: "单镜像包含 dashboard、worker 和 gateway",
							llmgateway: true,
							competitor: "仅 Gateway",
						},
					],
				},
				{
					category: "路由与可靠性",
					features: [
						{
							title: "自动供应商评分",
							description: "基于实时 uptime、吞吐、价格和延迟路由",
							llmgateway: true,
							competitor: "手动配置",
						},
						{
							title: "故障回退与重试",
							description: "自动在健康供应商上重试",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "负载均衡",
							description: "将同一模型的流量分散到多个供应商",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "响应缓存",
							description: "为重复请求提供内置缓存",
							llmgateway: "Redis，10 秒至 1 年 TTL",
							competitor: "含语义缓存",
						},
					],
				},
				{
					category: "成本与价格",
					features: [
						{
							title: "自带密钥",
							description: "使用自己的供应商 key",
							llmgateway: "0% 加价",
							competitor: true,
						},
						{
							title: "透明平台费",
							description: "可预测、容易理解的定价",
							llmgateway: "5% 或 0%（BYOK）",
							competitor: "用量/席位阶梯",
						},
						{
							title: "实时成本分析",
							description: "仪表盘中追踪每次请求成本",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "免费开始",
							description: "无需付费方案即可开始",
							llmgateway: "自托管免费",
							competitor: "免费层（有限制）",
						},
					],
				},
				{
					category: "能力覆盖",
					features: [
						{
							title: "模型覆盖",
							description: "通过一个 API 可用的模型和供应商",
							llmgateway: "300+ 模型，25+ 供应商",
							competitor: "1,600+（供应商声明）",
						},
						{
							title: "图像和视频生成",
							description: "通过同一 API 生成媒体内容",
							llmgateway: true,
							competitor: "有限",
						},
						{
							title: "安全护栏",
							description: "Prompt injection、PII、越狱和密钥检测",
							llmgateway: true,
							competitor: true,
						},
						{
							title: "版本化 prompt 管理",
							description: "Prompt registry、模板和部署",
							llmgateway: "仅 Playground",
							competitor: true,
						},
						{
							title: "OpenTelemetry 与深度 tracing",
							description: "将 traces 导出到现有观测系统",
							llmgateway: false,
							competitor: true,
						},
					],
				},
			],
		},
	},
};

const metadata: Record<Locale, Record<Competitor, CompareMetadata>> = {
	en: {
		openrouter: {
			title: "doteb vs OpenRouter — Feature Comparison",
			description:
				"Compare doteb's advanced routing, analytics, and cost optimization features against OpenRouter's basic proxy service. See why developers choose our unified API gateway for production LLM applications.",
			openGraphTitle: "doteb vs OpenRouter - Feature Comparison",
			openGraphDescription:
				"Compare doteb's advanced routing, analytics, and cost optimization features against OpenRouter's basic proxy service. See why developers choose our unified API gateway for production LLM applications.",
			twitterTitle: "doteb vs OpenRouter - Feature Comparison",
			twitterDescription:
				"Compare doteb's advanced routing, analytics, and cost optimization features against OpenRouter's basic proxy service.",
		},
		litellm: {
			title: "doteb vs LiteLLM — Feature Comparison",
			description:
				"Compare doteb's managed infrastructure, advanced analytics, and enterprise features against LiteLLM's self-hosted proxy solution. See why teams choose our production-ready API gateway.",
			openGraphTitle: "doteb vs LiteLLM - Feature Comparison",
			openGraphDescription:
				"Compare doteb's managed infrastructure, advanced analytics, and enterprise features against LiteLLM's self-hosted proxy solution. See why teams choose our production-ready API gateway.",
			twitterTitle: "doteb vs LiteLLM - Feature Comparison",
			twitterDescription:
				"Compare doteb's managed infrastructure, advanced analytics, and enterprise features against LiteLLM's self-hosted proxy solution.",
		},
		portkey: {
			title: "doteb vs Portkey — The Open Portkey Alternative",
			description:
				"Compare doteb's full-platform deployment, automatic provider routing, image and video generation, and transparent pricing against Portkey's gateway and LLMOps suite. See why teams pick doteb as their Portkey alternative.",
			openGraphTitle: "doteb vs Portkey - Feature Comparison",
			openGraphDescription:
				"Compare doteb's full-platform deployment, automatic provider routing, and transparent pricing against Portkey's gateway and LLMOps suite.",
			twitterTitle: "doteb vs Portkey - Feature Comparison",
			twitterDescription:
				"Compare doteb's full-platform deployment, automatic provider routing, and transparent pricing against Portkey's gateway and LLMOps suite.",
		},
	},
	"zh-CN": {
		openrouter: {
			title: "doteb vs OpenRouter - 功能对比",
			description:
				"对比 doteb 的高级路由、分析和成本优化能力，与 OpenRouter 基础代理服务的差异。",
			openGraphTitle: "doteb vs OpenRouter - 功能对比",
			openGraphDescription:
				"对比 doteb 的高级路由、分析和成本优化能力，与 OpenRouter 基础代理服务的差异。",
			twitterTitle: "doteb vs OpenRouter - 功能对比",
			twitterDescription:
				"对比 doteb 的高级路由、分析和成本优化能力，与 OpenRouter 基础代理服务的差异。",
		},
		litellm: {
			title: "doteb vs LiteLLM - 功能对比",
			description:
				"对比 doteb 的托管基础设施、高级分析和企业能力，与 LiteLLM 自托管代理方案的差异。",
			openGraphTitle: "doteb vs LiteLLM - 功能对比",
			openGraphDescription:
				"对比 doteb 的托管基础设施、高级分析和企业能力，与 LiteLLM 自托管代理方案的差异。",
			twitterTitle: "doteb vs LiteLLM - 功能对比",
			twitterDescription:
				"对比 doteb 的托管基础设施、高级分析和企业能力，与 LiteLLM 自托管代理方案的差异。",
		},
		portkey: {
			title: "doteb vs Portkey - 开放的 Portkey 替代方案",
			description:
				"对比 doteb 的完整平台部署、自动供应商路由、图像和视频生成以及透明价格，与 Portkey gateway 和 LLMOps 套件的差异。",
			openGraphTitle: "doteb vs Portkey - 功能对比",
			openGraphDescription:
				"对比 doteb 的完整平台部署、自动路由和透明计费，与 Portkey gateway 和 LLMOps 套件的差异。",
			twitterTitle: "doteb vs Portkey - 功能对比",
			twitterDescription:
				"对比 doteb 的完整平台部署、自动路由和透明计费，与 Portkey gateway 和 LLMOps 套件的差异。",
		},
	},
};

function localeKey(locale: Locale): Locale {
	return locale === "zh-CN" ? "zh-CN" : "en";
}

export function getCompareHeroContent(
	competitor: Competitor,
	locale: Locale,
): HeroContent {
	return heroContent[localeKey(locale)][competitor];
}

export function getComparisonContent(
	competitor: Competitor,
	locale: Locale,
): ComparisonContent {
	return comparisonContent[localeKey(locale)][competitor];
}

export function getCompareMetadata(
	competitor: Competitor,
	locale: Locale,
): CompareMetadata {
	return metadata[localeKey(locale)][competitor];
}
